import { NavLink, useNavigate } from "react-router-dom";
import { Activity, LayoutDashboard, FileStack, LogOut, ShieldCheck, UserCircle2, Users as UsersIcon, Eye } from "lucide-react";
import { useAuth } from "@/sales/auth/AuthContext";

const BASE_NAV = [
  { to: "/sales", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  {
    to: "/sales/gouden-standaard",
    label: "Gouden Standaard",
    icon: FileStack,
    testid: "nav-golden-standard",
  },
];

const SUPER_ADMIN_NAV = [
  { to: "/sales/gebruikers", label: "Gebruikers", icon: UsersIcon, testid: "nav-users" },
];

export default function Header() {
  const { user, logout, isBestuurslid, isGast } = useAuth();
  const navigate = useNavigate();

  const navItems = isBestuurslid ? [...BASE_NAV, ...SUPER_ADMIN_NAV] : BASE_NAV;

  const handleLogout = () => {
    logout();
    navigate("/sales/login", { replace: true });
  };

  const roleMeta = isBestuurslid
    ? {
        label: "Bestuurslid",
        className:
          "bg-gradient-to-r from-[#A07527] to-[#D4A24C] text-[#1C1407] border border-[#E2C77C]/60",
      }
    : isGast
    ? {
        label: "Gast",
        className: "bg-[#1E293B] text-[#94A3B8] border border-[#475569]",
      }
    : {
        label: "Reseller",
        className: "bg-[#1C3A5A] text-[#93C5FD] border border-[#3B6FB5]/60",
      };

  return (
    <header
      className="w-full border-b border-[rgba(192,192,192,0.12)] bg-[#0B1521]/85 backdrop-blur-md sticky top-0 z-20"
      data-testid="app-header"
    >
      <div className="w-full max-w-[1680px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <NavLink to="/sales" className="flex items-center gap-3 flex-shrink-0" data-testid="brand-link">
          <img
            src="https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/2bbd7681a8a04908b46aa22122b6939c_Gemini_Generated_Image_32yxxe32yxxe32yx.png"
            alt="RiskaiΛ brand mark"
            data-testid="brand-logo"
            style={{
              height: "48px",
              width: "auto",
              mixBlendMode: "lighten",
              WebkitMaskImage:
                "radial-gradient(circle at center, black 85%, transparent 100%)",
              maskImage:
                "radial-gradient(circle at center, black 85%, transparent 100%)",
            }}
            className="select-none"
          />
          <div>
            <h1 className="font-serif-display text-xl md:text-2xl font-semibold tracking-tight text-[#F8FAFC]">
              Riskai<span className="text-[#C0C0C0]">Λ</span>
            </h1>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] -mt-0.5">
              Sales Control Center
            </p>
          </div>
        </NavLink>

        <nav className="hidden md:flex items-center gap-1 bg-[#142539]/70 border border-[rgba(192,192,192,0.12)] rounded-full p-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/sales"}
              data-testid={testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] font-medium transition-all ${
                  isActive
                    ? "bg-[#1C3A5A] text-[#F8FAFC] border border-[rgba(192,192,192,0.3)]"
                    : "text-[#94A3B8] hover:text-[#E2E8F0]"
                }`
              }
            >
              <Icon size={14} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          {user && (
            <div
              data-testid="header-user"
              className="hidden sm:flex items-center gap-2.5 bg-[#142539]/70 border border-[rgba(192,192,192,0.12)] rounded-full pl-3 pr-1.5 py-1"
            >
              <UserCircle2 size={16} className="text-[#C0C0C0]" />
              <span
                data-testid="header-username"
                className="text-xs font-medium text-[#E2E8F0]"
              >
                {user.display_name}
              </span>
              <span
                data-testid="header-role-badge"
                className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 font-semibold ${roleMeta.className}`}
              >
                {isGast ? <Eye size={10} /> : <ShieldCheck size={10} />}
                {roleMeta.label}
              </span>
              <button
                type="button"
                data-testid="logout-btn"
                onClick={handleLogout}
                title="Log uit"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#94A3B8] hover:text-rose-300 border border-[rgba(192,192,192,0.18)] hover:border-rose-400/60 rounded-full px-2.5 py-1 transition-colors ml-1"
              >
                <LogOut size={11} /> Uit
              </button>
            </div>
          )}
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#94A3B8]">
            <Activity size={14} className="text-emerald-400" />
            <span className="uppercase tracking-[0.18em]">Live</span>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <nav className="md:hidden flex items-center gap-1 px-6 pb-3">
        {navItems.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/sales"}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] uppercase tracking-wider font-medium ${
                isActive
                  ? "bg-[#1C3A5A] text-[#F8FAFC] border border-[rgba(192,192,192,0.3)]"
                  : "text-[#94A3B8] border border-[rgba(192,192,192,0.12)]"
              }`
            }
          >
            <Icon size={13} />
            {label}
          </NavLink>
        ))}
        {user && (
          <button
            type="button"
            data-testid="logout-btn-mobile"
            onClick={handleLogout}
            className="inline-flex items-center gap-1 px-3 py-2 rounded text-[11px] uppercase tracking-wider font-medium text-[#94A3B8] border border-[rgba(192,192,192,0.12)]"
          >
            <LogOut size={13} />
          </button>
        )}
      </nav>
    </header>
  );
}
